<template>
  <VideoPlayerOverlaysWrapper
    v-if="activeOverlay"
    data-ui-section="overlay"
  >
    <Component
      :is="presentationComponent"
      v-if="presentationComponent && presentationOverlay"

      ref="overlayInner"
      :data-ui-section="presentationOverlay.name"

      :video="videoStoreState.video"
      :error="videoStoreState.error"
      :overlay="presentationOverlay"

      @shown="shown"
      @action="action"
      @dismiss="dismiss"
    />

    <OverlayLoadingOverlay
      v-else

      :video="videoStoreState.video"
      :overlay="activeOverlay"
    />
  </VideoPlayerOverlaysWrapper>
</template>

<script lang="ts">
import Vue, { VueConstructor } from 'vue';
import * as VideoStore from '@/scripts/store/modules/video';
import * as VideoPlayerStore from '@/scripts/store/modules/videoPlayer';
import { VideoOverlay, VideoOverlayType } from '@/scripts/store/modules/video/state/VideoOverlay';
import { VideoLoadingError } from '@/scripts/store/modules/video/state/errors';
import { Nullable } from '@/scripts/models/utility-types';
import { trackUiEvent, UiEventType } from './VideoPlayerOverlaysManager/helpers/trackerHelper';
import { importPresenterWithOverlay } from './VideoPlayerOverlaysManager/controllers/presenterSwitch';
import { VideoPlayerOverlayDismissPayload, VideoPlayerOverlayPerformActionPayload, VideoPlayerOverlayShownPayload } from './VideoPlayerOverlaysManager/presenters/layout/VideoPlayerOverlayBase';
import VideoPlayerOverlaysWrapper from './VideoPlayerOverlaysManager/presenters/layout/VideoPlayerOverlaysWrapper.ts.vue';
import OverlayLoadingOverlay from './VideoPlayerOverlaysManager/presenters/views/OverlayLoadingOverlay.ts.vue';

const fireAndForget = (fn: Function): void => {
  try {
    fn();
  } catch (error) {
    // Ignore
  }
};

export default Vue.extend({
  components: {
    VideoPlayerOverlaysWrapper,
    OverlayLoadingOverlay,
  },

  data() {
    return {
      presentationComponent: null as Nullable<VueConstructor>,
      presentationOverlay: null as Nullable<VideoOverlay>,
    };
  },

  computed: {
    videoStoreState: VideoStore.mapState(),
    activeOverlay: VideoStore.mapGetter('activeOverlay'),
    player: VideoPlayerStore.mapGetter('player'),
  },

  watch: {
    activeOverlay: {
      immediate: true,
      async handler(overlay: VideoOverlay | null): Promise<void> {
        // Always clear presenter when activeOverlay has changed
        this.presentationComponent = null;
        this.presentationOverlay = null;

        if (overlay == null) return;

        fireAndForget(() => {
          this.player?.pause();
        });

        try {
          this.presentationComponent = await importPresenterWithOverlay(overlay);
          this.presentationOverlay = overlay;
        } catch (error) {
          this.setVideoPlayerError({
            error: new VideoLoadingError(),
            issuer: VideoLoadingError,
          });
        };
      },
    },
  },

  methods: {
    removeVideoPlayerOverlay: VideoStore.mapMutation('removeVideoPlayerOverlay'),
    setVideoPlayerError: VideoStore.mapMutation('setVideoPlayerError'),

    shown(payload: VideoPlayerOverlayShownPayload): void {
      trackUiEvent(UiEventType.Shown, payload, this.$refs.overlayInner?.$el);
    },

    action(payload: VideoPlayerOverlayPerformActionPayload): void {
      trackUiEvent(UiEventType.Action, payload, this.$refs.overlayInner?.$el);
    },

    dismiss(payload: VideoPlayerOverlayDismissPayload): void {
      if (payload.autoplay) {
        if (this.videoStoreState.overlays.length <= 1) {
          fireAndForget(() => {
            this.player?.play();
          });
        }
      }

      if (payload.overlay.type !== VideoOverlayType.Dismissable) {
        return;
      }

      trackUiEvent(UiEventType.Dismiss, payload, this.$refs.overlayInner?.$el);
      this.removeVideoPlayerOverlay(payload.overlay);
    },
  },
});
</script>
